import { useState } from "react";
import { Link } from "react-router-dom";
import { SEO } from "../components/SEO";
import { Reveal } from "../components/Reveal";
import { CTASection } from "../components/CTASection";
import { ARTICLES } from "../data/articles";
import { INSIGHT_CATEGORIES } from "../data/content";

export default function Insights() {
  const [category, setCategory] = useState("All");
  const visible = category === "All" ? ARTICLES : ARTICLES.filter((a) => a.category === category);

  return (
    <>
      <SEO
        title="Insights | ZasyaOnline"
        description="Articles on transport and fleet operations, AI-powered business communication, enterprise learning, contact-centre consulting, and crane rental operations."
        path="/insights"
      />
      <section className="pt-28 pb-14 sm:pt-36 sm:pb-16" data-testid="insights-hero">
        <div className="container-x max-w-3xl">
          <Reveal>
            <span className="eyebrow">Insights</span>
            <h1 className="mt-5 font-display text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">
              Notes from the industries we build for.
            </h1>
            <p className="mt-5 text-lg leading-relaxed text-slate-600">
              Practical writing on fleet operations, AI business communication,
              enterprise learning, contact centres and crane rental — from the team
              that ships products in all five.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-16 sm:py-20" data-testid="insights-list">
        <div className="container-x">
          <Reveal>
            <div className="flex flex-wrap gap-2" role="group" aria-label="Filter articles by category">
              {["All", ...INSIGHT_CATEGORIES].map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setCategory(c)}
                  aria-pressed={category === c}
                  className={`rounded-full border px-4 py-2 text-xs font-semibold transition-colors duration-150 ${
                    category === c
                      ? "border-blue-600 bg-blue-600 text-white"
                      : "border-slate-300 bg-white text-slate-600 hover:border-blue-400 hover:text-blue-700"
                  }`}
                  data-testid={`insights-filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  {c}
                </button>
              ))}
            </div>
          </Reveal>
          <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {visible.map((a, i) => (
              <Reveal key={a.slug} delay={Math.min(i * 0.06, 0.25)} className="h-full">
                <Link
                  to={`/insights/${a.slug}`}
                  className="group card-line flex h-full flex-col overflow-hidden transition-[border-color,transform,box-shadow] duration-300 hover:-translate-y-1 hover:border-blue-500/60 hover:shadow-xl hover:shadow-blue-600/10"
                  data-testid={`insights-card-${a.slug}`}
                >
                  <div className="h-48 overflow-hidden">
                    <img
                      src={a.image}
                      alt={a.imageAlt}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-teal-700">
                      {a.category}
                    </span>
                    <h2 className="mt-2 font-display text-lg font-bold leading-snug tracking-tight text-slate-900">
                      {a.title}
                    </h2>
                    <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">{a.excerpt}</p>
                    <div className="mt-4 flex items-center justify-between text-xs text-slate-400">
                      <span>{a.author}</span>
                      <span>
                        {new Date(a.date).toLocaleDateString("en-IN", {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        })}
                      </span>
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTASection
        eyebrow="Go deeper"
        title="Prefer a conversation to an article?"
        body="Every topic here maps to a product or practice we run daily. Ask us directly."
      />
    </>
  );
}
